import { z } from "zod";
import { isValidTimeZone } from "../../utils/is-valid-time-zone.js";
import { emailSchema } from "../common/index.js";
import { EventAudience, EventFormat, EventRecurrence, EventVisibility } from "../enum.js";

export const createEventSchema = z
	.object({
		title: z
			.string()
			.min(2, { message: "Title must be at least 2 characters" })
			.max(200, { message: "Title must be at most 200 characters" }),
		description: z.string().max(10000).optional(),
		cover_image: z.url({ message: "Invalid cover image URL" }).optional(),
		recurrence: z.enum(EventRecurrence).default(EventRecurrence.PUNCTUAL),
		format: z.enum(EventFormat),
		start_at: z.iso.datetime({ message: "Start date is required" }),
		end_at: z.iso.datetime({ message: "End date is required" }),
		timezone: z.string().min(1).max(100).refine(isValidTimeZone, { message: "Invalid timezone" }).optional(),
		address: z.string().max(500).optional(),
		latitude: z.number().min(-90).max(90).optional(),
		longitude: z.number().min(-180).max(180).optional(),
		max_capacity: z.number().int().min(1, { message: "Capacity must be at least 1" }).optional(),
		registration_fee: z.number().min(0, { message: "Fee must be positive" }).optional(),
		audience: z.enum(EventAudience).default(EventAudience.ENTIRE),
		group_ids: z.array(z.uuid()).optional(),
		member_ids: z.array(z.uuid()).optional(),
		visibility: z.enum(EventVisibility).default(EventVisibility.PRIVATE),
		external_emails: z.array(emailSchema).max(50).optional(),
	})
	.strict()
	.refine((data) => new Date(data.end_at) > new Date(data.start_at), {
		message: "End time must be after start time",
		path: ["end_at"],
	})
	.refine((data) => data.format === EventFormat.ONLINE || data.address, {
		message: "Address is required for in-person or hybrid events",
		path: ["address"],
	})
	.refine((data) => data.audience !== EventAudience.GROUPS || (data.group_ids && data.group_ids.length > 0), {
		message: "At least one group is required",
		path: ["group_ids"],
	})
	.refine((data) => data.audience !== EventAudience.MEMBERS || (data.member_ids && data.member_ids.length > 0), {
		message: "At least one member is required",
		path: ["member_ids"],
	});

export type CreateEventPayload = z.infer<typeof createEventSchema>;
